import { isErrandReactionPending } from '@backend/schemas/src/errands'
import type { ErrandReactionPending } from '@backend/schemas/src/errands'
import { useQueryClient } from '@tanstack/react-query'
import { onValue } from 'firebase/database'
import { useEffect, useRef } from 'react'

import { getRealtimeRef } from '@/integrations/firebase'
import { useAppDispatch } from '@/redux/hooks'
import { AssistantActions } from '@/redux/reducers/assistant'

import { useThreadsServiceGetV1IndividualsByIndividualIdThreadsByThreadIdKey } from '../client/queries/common'

type ErrandRealtimeNode = {
  reactionAt?: string
  reactionPending?: ErrandReactionPending
}

/**
 * Brings proactive errand messages into an already-open chat.
 *
 * The backend writes an automated assistant message straight into the thread when documents
 * finish analysing or the errand changes status, then stamps `reactionAt` in the realtime
 * database. The chat's message list is local `useChat` state seeded from the thread query, so
 * without this the new message would only appear on the next page load.
 *
 * `canRefresh` must be false while the chat is streaming: refetching swaps the message list for
 * the persisted one, which would truncate a reply still being written.
 */
export const useErrandReactionRealtime = (params: {
  canRefresh: boolean
  errandId?: string
  individualId?: string
}) => {
  const { canRefresh, errandId, individualId } = params
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  // The baseline is keyed to the ERRAND, not to the effect run. Re-subscribing for the same errand
  // must not re-baseline: a fresh subscription's first snapshot reports current state, so adopting
  // it silently marks a reaction that landed while unsubscribed as "already seen". `undefined`
  // reactionAt is a real value here (an errand nobody has reacted to yet), which is why the errand
  // id is what says whether a baseline exists — "no value seen" cannot stand in for "no snapshot
  // received".
  //
  // The gap is not hypothetical. `selectedErrandId` comes from redux, set by an errand-page effect
  // that also depends on the fetched errand, so any errands refetch briefly clears and restores
  // it — and the note path does exactly that (`notesUpdatedAt`) a second or two before its
  // reaction lands.
  const seen = useRef<{ errandId: string; reactionAt?: string }>(undefined)
  // Read through a ref so a change to it doesn't tear down the subscription.
  const canRefreshRef = useRef(canRefresh)
  canRefreshRef.current = canRefresh
  // Which errand we have actually watched a reaction start on. `reactionPending` going empty only
  // means "the backend has finished deciding" once it has been seen non-empty — the flag is set
  // client-side the instant an upload succeeds, long before analysis finishes and the reaction is
  // even dispatched, so an empty map at that point means "not started", not "nothing coming".
  const sawPending = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!errandId || !individualId) return

    const ref = getRealtimeRef(`individuals/${individualId}/errands/${errandId}`)

    return onValue(ref, snapshot => {
      const value = snapshot.exists() ? (snapshot.val() as ErrandRealtimeNode) : undefined

      // The backend holds an entry here for each reaction it is composing and drops it on every
      // path that stops composing one — including deciding there is nothing worth saying. Watching
      // it empty out is what distinguishes "done, and said nothing" from "still working", which
      // `reactionAt` alone cannot say: a reaction that writes no message never stamps it, so
      // without this the indicator would sit until the 120s safety net gave up.
      if (isErrandReactionPending(value?.reactionPending)) {
        sawPending.current = errandId
      } else if (sawPending.current === errandId) {
        sawPending.current = undefined
        dispatch(AssistantActions.setPendingErrandReaction(undefined))
      }

      const reactionAt = value?.reactionAt
      const previous = seen.current

      // First snapshot for this errand: whatever is there now is what the thread query fetched
      // anyway, so adopt it rather than refetching for it.
      if (previous?.errandId !== errandId) {
        seen.current = { errandId, reactionAt }

        return
      }

      if (!reactionAt || reactionAt === previous.reactionAt) return
      seen.current = { errandId, reactionAt }

      // The wait is over regardless of `canRefresh` — a loading indicator keyed on this errand
      // should clear the moment the reaction actually lands, not only once the thread refetches.
      dispatch(AssistantActions.setPendingErrandReaction(undefined))

      if (!canRefreshRef.current) return

      queryClient.invalidateQueries({
        queryKey: [useThreadsServiceGetV1IndividualsByIndividualIdThreadsByThreadIdKey],
      })
    })
  }, [errandId, individualId, queryClient, dispatch])
}
